import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Check, RefreshCw, Loader2, Eye, Pencil, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useBlastMutations, useBlastMailboxes, useComposeMeta, type BlastRecipient } from "@/hooks/useBlast";

// Drafts are stored as simple <p> HTML; edit them as plain text.
const htmlToText = (h: string) =>
  h.replace(/<p[^>]*>/g, "").replace(/<\/p>/g, "\n\n").replace(/<br\s*\/?>/g, "\n").replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n").trim();
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
// Firm rule: no em/en dashes (macOS smart punctuation can insert them while
// typing here). The server re-strips at send time as the final guarantee.
const stripDashes = (s: string) =>
  s.replace(/\s*[—–]\s*/g, ", ").replace(/\s+--?\s+/g, ", ").replace(/;\s*/g, ", ").replace(/…|\.\.\./g, ".");
const textToHtml = (t: string) =>
  stripDashes(t).split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p style="margin:0 0 8px 0">${escapeHtml(p).replace(/\n/g, "<br>")}</p>`).join("");

export function DraftReviewDialog({
  campaignId, recipients, open, onOpenChange,
}: {
  campaignId: string;
  recipients: BlastRecipient[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const m = useBlastMutations();
  const { data: meta } = useComposeMeta();
  const { data: mailboxes = [] } = useBlastMailboxes();
  const withDrafts = useMemo(() => recipients.filter((r) => r.custom_body), [recipients]);
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState<"preview" | "edit">("preview");
  const current = withDrafts[Math.min(idx, Math.max(0, withDrafts.length - 1))];

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [dirty, setDirty] = useState(false);

  // Render the email exactly as the recipient will see it: draft body +
  // rotating-mailbox signature (CID logo swapped for the data URI) + firm
  // disclosure. Mirrors the server's composeHtml.
  const previewMailbox = mailboxes.find((mb) => mb.isActive) ?? mailboxes[0] ?? null;
  const previewHtml = useMemo(() => {
    const rawSig = previewMailbox?.signatureHtml ?? "";
    const sig = rawSig ? `<br><br>${meta ? rawSig.replace(/cid:ftc-logo/g, meta.logoDataUri) : rawSig}` : "";
    return `${textToHtml(body)}${sig}${meta?.complianceFooterHtml ?? ""}`;
  }, [body, previewMailbox, meta]);

  useEffect(() => {
    if (!current) return;
    setSubject(current.custom_subject ?? "");
    setBody(htmlToText(current.custom_body ?? ""));
    setDirty(false);
  }, [current?.id, current?.custom_body, current?.custom_subject]);

  if (!current) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review drafts</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">No drafts yet — generate them first.</p>
        </DialogContent>
      </Dialog>
    );
  }

  const approvedCount = withDrafts.filter((r) => r.draft_status === "approved").length;

  const saveEdits = async (): Promise<boolean> => {
    if (!dirty) return true;
    try {
      await m.updateRecipient.mutateAsync({
        campaignId, recipientId: current.id,
        customSubject: subject, customBody: textToHtml(body),
      });
      setDirty(false);
      return true;
    } catch (e: any) {
      toast.error(String(e?.message ?? e));
      return false;
    }
  };

  const approve = async () => {
    if (!(await saveEdits())) return;
    try {
      await m.updateRecipient.mutateAsync({ campaignId, recipientId: current.id, draftStatus: "approved" });
      if (idx < withDrafts.length - 1) setIdx(idx + 1);
    } catch (e: any) { toast.error(String(e?.message ?? e)); }
  };

  const regenerate = async () => {
    try {
      const r = await m.generateDrafts.mutateAsync({ id: campaignId, recipientIds: [current.id] });
      if (r.failed.length) toast.error("Regeneration failed — try again");
      else toast.success("Draft regenerated");
    } catch (e: any) { toast.error(String(e?.message ?? e)); }
  };

  const busy = m.updateRecipient.isPending || m.generateDrafts.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            Review drafts
            <span className="text-sm font-normal text-muted-foreground">
              {idx + 1} of {withDrafts.length} · {approvedCount} approved
            </span>
            <div className="flex gap-1 ml-auto mr-6">
              {(["preview", "edit"] as const).map((v) => (
                <Button key={v} type="button" size="sm" variant={mode === v ? "default" : "outline"} className="h-7 text-xs gap-1"
                  onClick={() => setMode(v)}>
                  {v === "preview" ? <Eye className="w-3 h-3" /> : <Pencil className="w-3 h-3" />}
                  {v === "preview" ? "Preview" : "Edit"}
                </Button>
              ))}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 min-w-0">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="min-w-0">
              <p className="font-medium truncate">
                {current.name || current.email}
                {current.company_name && <span className="text-muted-foreground font-normal"> · {current.company_name}</span>}
              </p>
              {current.lead_hook && <p className="text-xs text-primary-text truncate">Leads with: {current.lead_hook}</p>}
              {current.context && <p className="text-xs text-muted-foreground truncate">Context: {current.context}</p>}
            </div>
            {current.draft_style && (
              <Badge variant="outline" className="shrink-0 text-[10px] capitalize text-muted-foreground">{current.draft_style}</Badge>
            )}
            <Badge variant="outline" className={cn("shrink-0 text-[10px]",
              current.draft_status === "approved"
                ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800"
                : "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800")}>
              {current.draft_status === "approved" ? "Approved" : "Needs review"}
            </Badge>
          </div>

          {mode === "edit" ? (
            <>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input className="mt-1" value={subject}
                  onChange={(e) => { setSubject(e.target.value); setDirty(true); }} />
              </div>
              <div>
                <Label className="text-xs">Body <span className="text-muted-foreground">(signature and disclosure are added automatically)</span></Label>
                <Textarea className="mt-1 text-sm" rows={10} value={body}
                  onChange={(e) => { setBody(e.target.value); setDirty(true); }} />
              </div>
            </>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 border-b bg-muted/30 text-sm space-y-0.5">
                <p><span className="text-muted-foreground inline-block w-12">From</span> {previewMailbox ? `${previewMailbox.displayName || "First Turn Capital"} <${previewMailbox.fromAddress}>` : "rotates per send"}</p>
                <p><span className="text-muted-foreground inline-block w-12">To</span> {current.name ? `${current.name} <${current.email}>` : current.email}</p>
                <p className="font-medium pt-0.5">{subject || "(no subject)"}</p>
              </div>
              <div
                className="px-5 py-4 text-sm max-h-[420px] overflow-y-auto bg-white text-neutral-900 dark:text-neutral-200"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" disabled={idx === 0 || busy}
                onClick={async () => { if (await saveEdits()) setIdx(idx - 1); }}>
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button size="sm" variant="outline" disabled={idx >= withDrafts.length - 1 || busy}
                onClick={async () => { if (await saveEdits()) setIdx(idx + 1); }}>
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1.5">
              {approvedCount < withDrafts.length && (
                <Button size="sm" variant="outline" disabled={busy || m.approveAllDrafts.isPending}
                  onClick={async () => {
                    if (!(await saveEdits())) return;
                    const remaining = withDrafts.length - approvedCount;
                    if (!window.confirm(`Approve ${remaining} draft${remaining > 1 ? "s" : ""} without reading ${remaining > 1 ? "them" : "it"} individually?`)) return;
                    m.approveAllDrafts.mutate(campaignId, {
                      onSuccess: (r) => { toast.success(`${r.approved} draft${r.approved === 1 ? "" : "s"} approved`); onOpenChange(false); },
                      onError: (e: any) => toast.error(String(e?.message ?? e)),
                    });
                  }}>
                  {m.approveAllDrafts.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCheck className="w-3.5 h-3.5 mr-1" />}
                  Approve all {withDrafts.length - approvedCount}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={regenerate} disabled={busy}>
                {m.generateDrafts.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Regenerate
              </Button>
              <Button size="sm" onClick={approve} disabled={busy || (current.draft_status === "approved" && !dirty)}>
                {m.updateRecipient.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                {dirty ? "Save & approve" : current.draft_status === "approved" ? "Approved" : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
